import { ExpandLess, ExpandMore } from '@mui/icons-material';
import {
  Typography,
  TextField,
  Stack,
  Autocomplete,
  Grid2 as Grid,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { FormikProps, useFormik } from 'formik';
import { forwardRef, useImperativeHandle, useState } from 'react';
import usStates from 'states-us';
import * as yup from 'yup';

import { MaskedTextField } from '@/components/common/MaskedTextField';
import { UpdateUserProfile, UserProfile } from '@/models/UserProfile';
import { useTranslationPrefix } from '@/translations/i18n';
import { toProperCase } from '@/utils/properCase';

export type UpdateUserProfileFormRef = FormikProps<UpdateUserProfile>;

export interface UpdateUserProfileFormProps {
  /**
   * Formik values to prefill the form with.
   */
  values: UserProfile | undefined;
  /**
   * Callback fired when the form is submitted.
   */
  onSubmit: (values: UpdateUserProfile) => void;
  /**
   * Hides the disabled, display-only Email field. Defaults to false (shown)
   * so existing callers are unaffected — opt in where vertical space is tight.
   */
  hideEmail?: boolean;
  /**
   * Collapses the marketing-consent description behind an expand/collapse
   * caret, showing only the title by default. Defaults to false (always
   * expanded) so existing callers are unaffected — opt in on mobile where
   * vertical space is tight.
   */
  collapsibleMarketingConsent?: boolean;
  /**
   * Vertical spacing (MUI Stack `spacing` units) between each field row.
   * Defaults to 5 (20px), the original desktop density — pass a tighter
   * value where vertical space is at a premium.
   */
  rowSpacing?: number;
}

export const UpdateUserProfileForm = forwardRef<
  UpdateUserProfileFormRef,
  UpdateUserProfileFormProps
>(({ values, onSubmit, hideEmail, collapsibleMarketingConsent, rowSpacing = 5 }, ref) => {
  const t = useTranslationPrefix('update_profile');
  const validationT = useTranslationPrefix('validation');
  const [isMarketingConsentExpanded, setIsMarketingConsentExpanded] = useState(false);

  const stateOptions = usStates.map((state) => state.abbreviation);
  const getStateOptionLabel = (option: string) =>
    usStates.find((state) => state.abbreviation === option)?.name ?? '';

  const validationSchema = yup.object({
    firstName: yup.string().required('Required'),
    lastName: yup.string().required('Required'),
    postalCode: yup
      .number()
      .test('max-5-digits', validationT('valid', { name: t('postal_code') }), (value) => {
        const length = value?.toString().length ?? 0;
        return length === 0 || length <= 5;
      })
      .typeError(validationT('valid', { name: t('postal_code') })),
    phoneNumber: yup
      .number()
      .test('exactly-10-digits', validationT('valid', { name: t('phone_number') }), (value) => {
        const length = value?.toString().length ?? 0;
        return length === 0 || length === 10;
      })
      .typeError(validationT('valid', { name: t('phone_number') })),
  });

  const formik = useFormik<UpdateUserProfile>({
    initialValues: {
      firstName: values?.firstName ?? '',
      lastName: values?.lastName ?? '',
      addressLine1: values?.addressLine1,
      addressLine2: values?.addressLine2,
      city: values?.city,
      state: values?.state,
      postalCode: values?.postalCode,
      phoneNumber: values?.phoneNumber,
      marketingOptIn: values?.marketingOptIn ?? false,
    },
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: false,
    onSubmit,
  });

  useImperativeHandle(ref, () => formik);

  return (
    <form onSubmit={formik.handleSubmit}>
      <Stack spacing={5}>
        <Grid container columns={{ sm: 1, md: 2 }} columnSpacing={9} rowSpacing={5}>
          <Grid size={1}>
            <Stack spacing={rowSpacing}>
              {/* First Name & Last Name */}
              <Stack direction="row" spacing={4}>
                <Stack spacing={1} sx={{ flex: 1 }}>
                  <Typography component="label" htmlFor="first-name-field" variant="body2">
                    {validationT('required_label', { name: t('first_name') })}
                  </Typography>
                  <TextField
                    id="first-name-field"
                    name="firstName"
                    aria-label={t('first_name')}
                    fullWidth
                    required
                    autoComplete="given-name"
                    autoFocus
                    value={formik.values.firstName}
                    onChange={(e) =>
                      formik.setFieldValue('firstName', toProperCase(e.target.value))
                    }
                    onBlur={formik.handleBlur}
                    error={Boolean(formik.errors.firstName)}
                  />
                </Stack>
                <Stack spacing={1} sx={{ flex: 1 }}>
                  <Typography component="label" htmlFor="last-name-field" variant="body2">
                    {validationT('required_label', { name: t('last_name') })}
                  </Typography>
                  <TextField
                    id="last-name-field"
                    name="lastName"
                    aria-label={t('last_name')}
                    fullWidth
                    required
                    autoComplete="family-name"
                    autoFocus
                    value={formik.values.lastName}
                    onChange={(e) => formik.setFieldValue('lastName', toProperCase(e.target.value))}
                    onBlur={formik.handleBlur}
                    error={Boolean(formik.errors.lastName)}
                  />
                </Stack>
              </Stack>

              {/* Phone Number */}
              <Stack spacing={1}>
                <Typography component="label" htmlFor="phoneNumber-field" variant="body2">
                  {t('phone_number')}
                </Typography>
                <MaskedTextField
                  id="phoneNumber-field"
                  name="phoneNumber"
                  aria-label={t('phone_number')}
                  fullWidth
                  autoComplete="tel"
                  maskProps={{
                    mask: '(A00) 000-0000',
                    definitions: {
                      A: /[2-9]/, // Area code cannot start with 0 or 1
                    },
                    lazy: false, // Make placeholder always visible
                    placeholderChar: t('phone_number_mask'),
                  }}
                  value={String(formik.values.phoneNumber)}
                  onChange={(value) =>
                    formik.setFieldValue('phoneNumber', value.length > 0 ? Number(value) : '')
                  }
                  onBlur={formik.handleBlur}
                  error={formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)}
                  helperText={formik.touched.phoneNumber && formik.errors.phoneNumber}
                />
              </Stack>

              {/* Email — disabled/display-only, so hideEmail consumers lose
                  no data-entry capability, just a read-only confirmation. */}
              {!hideEmail && (
                <Stack spacing={1}>
                  <Typography component="label" htmlFor="email-field" variant="body2">
                    {t('email')}
                  </Typography>
                  <TextField
                    name="email"
                    disabled
                    placeholder={t('email')}
                    aria-label={t('email')}
                    autoComplete="email"
                    autoFocus
                    value={values?.email}
                  />
                </Stack>
              )}
            </Stack>
          </Grid>

          <Grid size={1}>
            <Stack spacing={rowSpacing}>
              {/* Address */}
              <Stack spacing={1}>
                <Typography component="label" htmlFor="addressLine1-field" variant="body2">
                  {t('address_line_1')}
                </Typography>
                <TextField
                  id="addressLine1-field"
                  name="addressLine1"
                  aria-label={t('address_line_1')}
                  fullWidth
                  autoComplete="address-line1"
                  value={formik.values.addressLine1}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.addressLine1 && Boolean(formik.errors.addressLine1)}
                  helperText={formik.touched.addressLine1 && formik.errors.addressLine1}
                />
              </Stack>

              {/* City */}
              <Stack spacing={1}>
                <Typography component="label" htmlFor="city-field" variant="body2">
                  {t('city')}
                </Typography>
                <TextField
                  id="city-field"
                  name="city"
                  aria-label={t('city')}
                  fullWidth
                  autoComplete="address-level2"
                  value={formik.values.city}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.city && Boolean(formik.errors.city)}
                  helperText={formik.touched.city && formik.errors.city}
                />
              </Stack>

              {/* State & Zip Code */}
              <Stack direction="row" spacing={4}>
                <Stack spacing={1} sx={{ flex: 1 }}>
                  <Typography component="label" htmlFor="state-field" variant="body2">
                    {t('state')}
                  </Typography>
                  <Autocomplete
                    id="state-field"
                    disablePortal
                    fullWidth
                    value={formik.values.state}
                    options={stateOptions}
                    getOptionLabel={getStateOptionLabel}
                    onChange={(_event, value) => formik.setFieldValue('state', value ?? '')}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        aria-label={t('state')}
                        error={formik.touched.state && Boolean(formik.errors.state)}
                        helperText={formik.touched.state && formik.errors.state}
                      />
                    )}
                  />
                </Stack>
                <Stack spacing={1} sx={{ flex: 1 }}>
                  <Typography component="label" htmlFor="postalCode-field" variant="body2">
                    {t('postal_code')}
                  </Typography>
                  <MaskedTextField
                    id="postalCode-field"
                    name="postalCode"
                    aria-label={t('postal_code')}
                    fullWidth
                    autoComplete="postal-code"
                    maskProps={{
                      mask: '00000',
                    }}
                    value={String(formik.values.postalCode)}
                    onChange={(value) =>
                      formik.setFieldValue('postalCode', value.length > 0 ? Number(value) : '')
                    }
                    onBlur={formik.handleBlur}
                    error={formik.touched.postalCode && Boolean(formik.errors.postalCode)}
                    helperText={formik.touched.postalCode && formik.errors.postalCode}
                  />
                </Stack>
              </Stack>
            </Stack>
          </Grid>
        </Grid>
        <Grid container columns={{ sm: 1, md: 2 }} columnSpacing={9} rowSpacing={5}>
          <Grid size={1}>
            <FormControlLabel
              control={
                <Checkbox
                  id="marketingOptIn-field"
                  name="marketingOptIn"
                  size="small"
                  checked={formik.values.marketingOptIn}
                  onChange={(event) => {
                    formik.setFieldValue('marketingOptIn', event.target.checked);
                  }}
                  onBlur={formik.handleBlur}
                  sx={{
                    mt: -1.75,
                    mr: 2.25,
                    '> svg': {
                      width: 20,
                      height: 20,
                    },
                  }}
                />
              }
              label={
                collapsibleMarketingConsent ? (
                  <Stack spacing={0.5}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={0.5}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setIsMarketingConsentExpanded((expanded) => !expanded);
                      }}>
                      <Typography variant="body3" color="text.secondary" fontWeight="600">
                        {t('marketing_consent_title')}
                      </Typography>
                      {isMarketingConsentExpanded ? (
                        <ExpandLess sx={{ fontSize: 18, color: 'text.secondary' }} />
                      ) : (
                        <ExpandMore sx={{ fontSize: 18, color: 'text.secondary' }} />
                      )}
                    </Stack>
                    {isMarketingConsentExpanded && (
                      <Typography variant="body3" color="text.secondary" lineHeight={1.3}>
                        {t('marketing_consent_description')}
                      </Typography>
                    )}
                  </Stack>
                ) : (
                  <Typography component="div" variant="body3" color="text.secondary" lineHeight={1.3}>
                    <Typography variant="inherit" fontWeight="600">
                      {t('marketing_consent_title')}
                    </Typography>
                    <Typography variant="inherit">{t('marketing_consent_description')}</Typography>
                  </Typography>
                )
              }
              sx={{
                alignItems: 'flex-start',
                ml: -2.25,
                mr: 0,
                py: 1,
              }}
            />
          </Grid>
        </Grid>
      </Stack>
    </form>
  );
});
